import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { initializeMessaging, requestNotificationPermission, onForegroundMessage, getFcmToken } from '@/config/firebase';
import { toast } from 'sonner';
import api from '@/services/api';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [fcmToken, setFcmToken] = useState(null);
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isSupported, setIsSupported] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastRegisteredUserId, setLastRegisteredUserId] = useState(null);
  
  // Refs to prevent duplicate API calls
  const isRegisteringToken = useRef(false);
  const lastTokenRegistrationTime = useRef(0);
  const TOKEN_REGISTRATION_COOLDOWN = 60000; // 1 minute cooldown between registrations

  // Check if notifications are supported
  useEffect(() => {
    const checkSupport = async () => {
      const supported = 'Notification' in window && 
                        'serviceWorker' in navigator && 
                        'PushManager' in window;
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
      }
    };
    
    checkSupport();
  }, []);

  // Initialize FCM when user logs in
  useEffect(() => {
    if (isAuthenticated && isSupported && !isInitialized) {
      initializeFCM();
    }
  }, [isAuthenticated, isSupported, isInitialized]);

  // Listen for foreground messages
  useEffect(() => {
    if (!isInitialized || !isAuthenticated) return;

    const unsubscribe = onForegroundMessage((payload) => {
      handleForegroundNotification(payload);
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [isInitialized, isAuthenticated]);

  // Listen for service worker messages
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK') {
        console.log('Notification clicked:', event.data);
        // Handle navigation or state updates here
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);
    
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, []);

  const initializeFCM = async () => {
    try {
      await initializeMessaging();
      setIsInitialized(true);
      console.log('FCM initialized successfully');
    } catch (error) {
      console.error('Failed to initialize FCM:', error);
    }
  };

  const sendTokenToBackend = useCallback(async (token) => {
    // Prevent duplicate/rapid API calls
    const now = Date.now();
    if (isRegisteringToken.current) {
      console.log('FCM token registration already in progress, skipping...');
      return;
    }
    if (now - lastTokenRegistrationTime.current < TOKEN_REGISTRATION_COOLDOWN) {
      console.log('FCM token registration on cooldown, skipping...');
      return;
    }
    
    // Don't call API if no auth token exists
    const authToken = localStorage.getItem('token');
    if (!authToken) {
      console.log('No auth token found, skipping device token registration...');
      return;
    }
    
    isRegisteringToken.current = true;
    
    try {
      const formData = new FormData();
      formData.append('device_token', token);

      await api.post('/update-device-token', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Accept': 'application/json',
        },
      });

      lastTokenRegistrationTime.current = now;
      console.log('FCM token registered with backend');
    } catch (error) {
      // Silently ignore 401/429 errors - don't disrupt user experience
      const status = error.response?.status;
      if (status === 401 || status === 429) {
        console.warn(`Device token registration failed with ${status}, ignoring...`);
      } else {
        console.error('Failed to register FCM token:', error);
      }
      // Don't throw - token still works for local notifications
    } finally {
      isRegisteringToken.current = false;
    }
  }, []);

  // Register token automatically if already granted after login
  // Uses refs to prevent duplicate API calls
  useEffect(() => {
    if (!isAuthenticated || !isSupported || permission !== 'granted') return;
    if (!user?.id) return;

    // Already registered for this user and token exists
    if (fcmToken && lastRegisteredUserId === user.id) {
      return;
    }
    
    // Prevent running if already in progress
    if (isRegisteringToken.current) {
      return;
    }

    const ensureTokenRegistered = async () => {
      const result = await getFcmToken();
      if (result.permission === 'granted' && result.token) {
        setFcmToken(result.token);
        await sendTokenToBackend(result.token);
        setLastRegisteredUserId(user.id);
      }
    };

    ensureTokenRegistered();
  }, [isAuthenticated, isSupported, permission, user?.id]); // Removed fcmToken, lastRegisteredUserId, sendTokenToBackend from deps

  const handleForegroundNotification = (payload) => {
    console.log('Foreground notification:', payload);
    
    const { notification, data } = payload;
    const title = notification?.title || data?.title || 'New Notification';
    const body = notification?.body || data?.body || '';
    const type = data?.type || 'general';
    
    // Add to notifications list
    const newNotification = {
      id: Date.now(),
      title,
      body,
      type,
      data,
      read: false,
      timestamp: new Date().toISOString()
    };
    
    setNotifications(prev => [newNotification, ...prev]);
    setUnreadCount(prev => prev + 1);
    
    // Show toast notification
    const toastOptions = {
      description: body,
      duration: 5000,
      action: data?.url ? {
        label: 'View',
        onClick: () => window.location.href = data.url
      } : undefined
    };
    
    switch (type) {
      case 'appointment':
        toast.info(title, { ...toastOptions, icon: '📅' });
        break;
      case 'message':
        toast.info(title, { ...toastOptions, icon: '💬' });
        break;
      case 'payment':
        toast.success(title, { ...toastOptions, icon: '💰' });
        break;
      case 'review':
        toast.info(title, { ...toastOptions, icon: '⭐' });
        break;
      case 'alert':
        toast.warning(title, { ...toastOptions, icon: '⚠️' });
        break;
      default:
        toast(title, toastOptions);
    }
    
    // Show browser notification if page is not focused
    if (document.hidden && permission === 'granted') {
      showBrowserNotification(title, body, data);
    }
  };

  const showBrowserNotification = (title, body, data = {}) => {
    if (permission !== 'granted') return;
    
    const notification = new Notification(title, {
      body,
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: data.tag || 'dactrha-notification',
      data
    });
    
    notification.onclick = () => {
      window.focus();
      if (data.url) {
        window.location.href = data.url;
      }
      notification.close();
    };
  };

  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      toast.error('Notifications are not supported in this browser');
      return { success: false, error: 'Not supported' };
    }
    
    try {
      const result = await requestNotificationPermission();
      setPermission(result.permission);
      
      if (result.permission === 'granted' && result.token) {
        setFcmToken(result.token);
        
        // Send token to backend
        await sendTokenToBackend(result.token);
        setLastRegisteredUserId(user?.id || null);
        
        toast.success('Notifications enabled successfully!');
        return { success: true, token: result.token };
      } else if (result.permission === 'denied') {
        toast.error('Notification permission was denied. Please enable it in browser settings.');
        return { success: false, error: 'Permission denied' };
      } else {
        toast.warning('Could not enable notifications. Please try again.');
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast.error('Failed to enable notifications');
      return { success: false, error };
    }
  }, [isSupported, sendTokenToBackend, user?.id]);

  

  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Test notification function (for development)
  const sendTestNotification = useCallback(() => {
    handleForegroundNotification({
      notification: {
        title: 'Test Notification',
        body: 'This is a test notification from Dactrha'
      },
      data: {
        type: 'appointment',
        url: '/appointments'
      }
    });
  }, []);

  const value = {
    // State
    fcmToken,
    permission,
    notifications,
    unreadCount,
    isSupported,
    isInitialized,
    
    // Actions
    requestPermission,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    sendTestNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
